<template>
  <button
    :class="[
      'base-button',
      `base-button--${variant}`,
      `base-button--${size}`,
      {
        'base-button--loading': loading,
        'base-button--disabled': disabled || loading
      }
    ]"
    :disabled="disabled || loading"
    :type="type"
    @click="handleClick"
  >
    <span v-if="loading" class="base-button__loader"></span>
    <slot v-else name="icon" />
    <span class="base-button__text">
      <slot />
    </span>
  </button>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'BaseButton',
  emits: ['click'],
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'danger', 'outline'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const handleClick = (event) => {
      if (!props.disabled && !props.loading) {
        emit('click', event)
      }
    }

    return {
      handleClick
    }
  }
})
</script>

<style scoped>
.base-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-md);
  font-family: var(--font-family-base);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
  outline: none;
  user-select: none;
}

.base-button:focus-visible {
  box-shadow: 0 0 0 3px var(--color-primary-light);
  outline: 2px solid transparent;
}

/* Sizes */
.base-button--sm {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-sm);
  min-height: 2rem;
}

.base-button--md {
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-base);
  min-height: 2.5rem;
}

.base-button--lg {
  padding: var(--space-4) var(--space-6);
  font-size: var(--font-size-lg);
  min-height: 3rem;
}

/* Primary Variant */
.base-button--primary {
  background-color: var(--color-primary);
  color: var(--color-white);
  border-color: var(--color-primary);
}

.base-button--primary:hover:not(.base-button--disabled) {
  background-color: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.base-button--primary:active:not(.base-button--disabled) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* Secondary Variant */
.base-button--secondary {
  background-color: var(--color-accent);
  color: var(--color-white);
  border-color: var(--color-accent);
}

.base-button--secondary:hover:not(.base-button--disabled) {
  background-color: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.base-button--secondary:active:not(.base-button--disabled) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* Danger Variant */
.base-button--danger {
  background-color: var(--color-error);
  color: var(--color-white);
  border-color: var(--color-error);
}

.base-button--danger:hover:not(.base-button--disabled) {
  background-color: #B91C1C;
  border-color: #B91C1C;
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.base-button--danger:active:not(.base-button--disabled) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* Outline Variant */
.base-button--outline {
  background-color: transparent;
  color: var(--color-primary);
  border-color: var(--color-primary);
}

.base-button--outline:hover:not(.base-button--disabled) {
  background-color: var(--color-primary);
  color: var(--color-white);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.base-button--outline:active:not(.base-button--disabled) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* Disabled State */
.base-button--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* Loading State */
.base-button--loading {
  cursor: not-allowed;
  pointer-events: none;
}

.base-button__text {
  display: inline-block;
}

.base-button--loading .base-button__text {
  opacity: 0.7;
}

/* Loading Spinner */
.base-button__loader {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-button {
    min-width: auto;
  }

  .base-button--sm {
    padding: var(--space-2) var(--space-2);
  }

  .base-button--md {
    padding: var(--space-2) var(--space-3);
  }

  .base-button--lg {
    padding: var(--space-3) var(--space-4);
  }
}
</style>